import { useQuery } from '@tanstack/react-query'
import { http } from '@/utils/httpCommon'
import { apiUrls } from '@/utils/apiUrls'
import { cloudWatchLogsApiResponseSchema, CloudWatchLogsApiResponse, CloudWatchTableRow } from '@/types/cloudWatch.type'
import { cloudWatchQueryKeys } from './queryKeys'

interface UseCloudWatchLogsParams {
  workstream: string
  statusCodes?: '4xx' | '5xx' | 'all'
  startDate: string // Format: YYYY-MM-DD
  endDate: string // Format: YYYY-MM-DD
  environment?: string
  limit?: number
  cursor?: string | null // Cursor for server-side pagination
}

/**
 * Hook to fetch CloudWatch logs
 * @param params - Query parameters including workstream, status codes, and date range
 * @returns CloudWatch logs data with flattened table rows
 */
export const useCloudWatchLogs = ({
  workstream,
  statusCodes = 'all',
  startDate,
  endDate,
  environment,
  limit,
  cursor,
}: UseCloudWatchLogsParams) => {
  return useQuery({
    queryKey: cloudWatchQueryKeys.logs({
      workstream,
      statusCodes: statusCodes === 'all' ? null : statusCodes,
      startDate,
      endDate,
      environment: environment && environment !== 'all' ? environment : null,
      cursor: cursor || null,
      limit: limit ?? null,
    }),
    queryFn: async () => {
      // Build query params
      const params: Record<string, string | number> = {
        workstream,
        start_date: startDate,
        end_date: endDate,
        limit: limit ?? 2000,
      }

      // Add status_codes parameter
      if (statusCodes === 'all') {
        params.status_codes = '4xx,5xx'
      } else {
        params.status_codes = statusCodes
      }

      // Add environment if specified
      if (environment && environment !== 'all') {
        params.environment = environment
      }

      // Add cursor for server-side pagination if provided
      if (cursor) {
        params.cursor = cursor
      }

      // Make API request using centralized apiUrls
      const { data } = await http.get<CloudWatchLogsApiResponse>(apiUrls.getCloudWatchLogs, { params })

      // Validate response with Zod
      const validatedData = cloudWatchLogsApiResponseSchema.parse(data)

      // Transform data to flattened table rows
      const tableRows: CloudWatchTableRow[] = []

      validatedData.lambda_logs.forEach((lambdaLog) => {
        lambdaLog.logs.forEach((log, index) => {
          // Parse message to extract readable error message
          let errorMessage = log.message
          let parsedMessage = null

          try {
            parsedMessage = JSON.parse(log.message)
            // For BackOffice workstream: use "message" field
            // For other workstreams: use "MessageTemplate" field
            if (workstream === 'back-office') {
              errorMessage = parsedMessage.message || log.message
            } else {
              errorMessage = parsedMessage.MessageTemplate || parsedMessage.message || log.message
            }
          } catch {
            // If JSON parsing fails, use raw message
            errorMessage = log.message
          }

          tableRows.push({
            id: `${lambdaLog.lambda_function_name}-${log.timestamp}-${index}`,
            error_message: errorMessage,
            error_code: log.status_code,
            lambda_function: lambdaLog.lambda_function_name,
            log_group: lambdaLog.log_group,
            stack_name: lambdaLog.stack_name,
            region: lambdaLog.region,
            timestamp: log.timestamp,
            full_message: log.message,
          })
        })
      })

      return {
        ...validatedData,
        tableRows,
        pagination: validatedData.pagination ?? null,
      }
    },
    enabled: !!workstream && !!startDate && !!endDate,
  })
}
